import axios from 'axios';
import { Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const YEARS = [2023, 2024, 2025, 2026];

/**
 * New Payment Modal — extracted from Budgets.js.
 * Preserves all employee search + assigned-projects fetching behavior.
 */
const NewPaymentModal = ({
  open, onClose, newRecord, setNewRecord,
  employeeSearch, setEmployeeSearch, filteredEmployees,
  employeeProjects, setEmployeeProjects,
  onSubmit, getAuthHeaders,
}) => {
  if (!open) return null;

  const handleSelectEmployee = async (u) => {
    setNewRecord({ ...newRecord, employee_email: u.email });
    setEmployeeSearch(u.artist_name || u.name);
    try {
      const res = await axios.get(`${API}/projects?status_filter=active`, { headers: getAuthHeaders() });
      const projects = res.data || [];
      const histRes = await axios.get(`${API}/projects?status_filter=history`, { headers: getAuthHeaders() });
      const allProjs = [...projects, ...(histRes.data || [])].filter(p => p.payment_status !== 'pago');
      const userProjects = [];
      for (const p of allProjs) {
        try {
          let entry = null;
          const charsRes = await axios.get(`${API}/projects/${p.id}/characters`, { headers: getAuthHeaders() });
          const userChars = (charsRes.data || []).filter(c =>
            c.assigned_user_id === u.id || (c.assigned_users || []).some(au => au.id === u.id)
          );
          if (userChars.length > 0) {
            const totalFalas = userChars.reduce((s, c) => s + (c.num_falas || 0), 0);
            entry = { id: p.id, title: p.title, characters: userChars.map(c => ({ name: c.name, falas: c.num_falas || 0 })), totalFalas, staffRoles: [], staffValue: 0 };
          }
          const durationMin = (p.duration_seconds || 0) / 60;
          if (durationMin > 0) {
            const pushRole = (label, mult, idsKey) => {
              const ids = p[idsKey] || [];
              if (ids.includes(u.id)) {
                const val = Math.round(((durationMin * mult) / ids.length) * 100) / 100;
                if (!entry) entry = { id: p.id, title: p.title, characters: [], totalFalas: 0, staffRoles: [], staffValue: 0 };
                entry.staffRoles.push({ label, value: val });
                entry.staffValue = Math.round((entry.staffValue + val) * 100) / 100;
              }
            };
            pushRole('Gerência', 4, 'responsible_admin_ids');
            pushRole('Mixagem', 4, 'mixadores_ids');
            pushRole('Roteiro', 2, 'roteiristas_ids');
            pushRole('SRT', 2, 'srt_ids');
          }
          if (entry) userProjects.push(entry);
        } catch (err) { console.error('Failed to fetch chars:', err); }
      }
      setEmployeeProjects(userProjects);
    } catch { setEmployeeProjects([]); }
  };

  const handleToggleProject = (p) => {
    const isSelected = newRecord.video_name.includes(p.title);
    let updatedName;
    if (isSelected) {
      const parts = newRecord.video_name.split(', ').filter(t => t.trim() && t.trim() !== p.title);
      updatedName = parts.join(', ');
    } else {
      const current = newRecord.video_name.trim();
      const sep = current && !current.endsWith(',') ? ', ' : '';
      updatedName = (current + sep + p.title).replace(/^,\s*/, '');
    }
    const updatedValue = employeeProjects
      .filter(ep => updatedName.includes(ep.title))
      .reduce((s, ep) => s + (ep.totalFalas || 0) + (ep.staffValue || 0), 0);
    setNewRecord({ ...newRecord, video_name: updatedName, value: Math.round(updatedValue * 100) / 100 || newRecord.value });
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md border border-slate-700 my-auto max-h-[calc(100vh-2rem)] overflow-y-auto">
        <h2 className="text-2xl font-bold text-white mb-6">Novo Pagamento</h2>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Funcionário *</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={employeeSearch}
                onChange={e => { setEmployeeSearch(e.target.value); setNewRecord({ ...newRecord, employee_email: '' }); }}
                placeholder="Buscar por nome ou email..."
                className="w-full bg-slate-700 border border-slate-600 rounded-lg pl-9 pr-4 py-2 text-white text-sm focus:outline-none focus:border-teal-500"
                data-testid="employee-search-input"
              />
            </div>
            <div className="mt-1 max-h-36 overflow-y-auto bg-slate-700 border border-slate-600 rounded-lg">
              {filteredEmployees.map(u => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => handleSelectEmployee(u)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-600 transition-colors flex items-center gap-2 ${newRecord.employee_email === u.email ? 'text-teal-400 bg-teal-500/10' : 'text-white'}`}
                >
                  {u.picture ? <img src={u.picture} alt="" className="w-5 h-5 rounded-full" /> : <div className="w-5 h-5 rounded-full bg-teal-500/20 text-teal-400 text-xs flex items-center justify-center">{u.name?.charAt(0)}</div>}
                  <span className="truncate">{u.artist_name || u.name}</span>
                  {u.artist_name && <span className="text-slate-500 text-xs">({u.name})</span>}
                </button>
              ))}
              {filteredEmployees.length === 0 && <p className="px-3 py-2 text-sm text-slate-500">Nenhum resultado</p>}
            </div>
            {newRecord.employee_email && <p className="mt-1 text-xs text-teal-400">Selecionado: {newRecord.employee_email}</p>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Mês *</label>
              <select value={newRecord.month} onChange={e => setNewRecord({ ...newRecord, month: parseInt(e.target.value) })}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500" required>
                {MONTHS.map((month, idx) => <option key={idx} value={idx + 1}>{month}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Ano *</label>
              <select value={newRecord.year} onChange={e => setNewRecord({ ...newRecord, year: parseInt(e.target.value) })}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500" required>
                {YEARS.map(year => <option key={year} value={year}>{year}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Projeto/Vídeo *</label>
            <input
              type="text"
              value={newRecord.video_name}
              onChange={e => setNewRecord({ ...newRecord, video_name: e.target.value })}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500"
              placeholder="Digite o nome do projeto/vídeo..."
              required
              data-testid="project-name-input"
            />
            {employeeProjects.length > 0 && (
              <div className="mt-2 bg-slate-800/80 border border-slate-600 rounded-lg overflow-hidden">
                <p className="px-3 py-1.5 text-xs text-slate-400 border-b border-slate-700 bg-slate-800">Projetos atribuídos (clique para adicionar):</p>
                <div className="max-h-32 overflow-y-auto">
                  {employeeProjects.map(p => {
                    const isSelected = newRecord.video_name.includes(p.title);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => handleToggleProject(p)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${isSelected ? 'bg-teal-500/15 text-teal-400' : 'text-slate-300 hover:bg-slate-700'}`}
                      >
                        <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isSelected ? 'bg-teal-500 border-teal-500 text-white' : 'border-slate-500'}`}>
                          {isSelected && <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                        </span>
                        <div className="flex-1 min-w-0">
                          <span className="font-medium block truncate">{p.title}</span>
                          <span className="text-slate-500 text-xs">
                            {p.characters.length > 0 && p.characters.map(c => `${c.name} (${c.falas}f)`).join(', ')}
                            {p.staffRoles.length > 0 && (p.characters.length > 0 ? ' · ' : '') + p.staffRoles.map(r => `${r.label} R$${r.value.toFixed(2)}`).join(' · ')}
                          </span>
                        </div>
                        <span className="text-teal-400 text-xs font-mono shrink-0">R$ {p.totalFalas.toFixed(2)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Valor a Receber (R$) *</label>
            <input type="number" step="0.01" min="0" value={newRecord.value} onChange={e => setNewRecord({ ...newRecord, value: e.target.value })}
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-teal-500" required data-testid="amount-input" />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-2 rounded-lg transition-colors">Cancelar</button>
            <button type="submit" className="flex-1 bg-teal-500 hover:bg-teal-600 text-white py-2 rounded-lg transition-colors" data-testid="submit-payment-btn">Registrar</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewPaymentModal;
